import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import {
  FiFeather, FiCheck, FiList, FiSidebar, FiLayers,
  FiClock, FiShield, FiGlobe, FiZap, FiExternalLink,
} from 'react-icons/fi';
import ChromeStoreBtn from '../components/ChromeStoreBtn';
import { getProduct } from '../data/catalog';

const PRODUCT = getProduct('promptwick');
const STORE_LINK = PRODUCT.storeLink;
const SITE_LINK = 'https://promptwick.com';
const PRICING_LINK = 'https://promptwick.com/#pricing';

// The eleven services the extension actually declares host permissions for. The
// manifest lists fifteen patterns because several of these answer on two hostnames
// (chatgpt.com and chat.openai.com, grok.com and x.ai, and so on).
const SITES = [
  'ChatGPT', 'Claude', 'Gemini', 'Grok', 'Perplexity', 'DeepSeek',
  'Poe', 'Microsoft Copilot', 'Meta AI', 'Mistral', 'You.com',
];

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-card border border-border p-6 rounded-none hover:border-accent-amber/50 transition-all group">
    <div className="w-12 h-12 rounded-md bg-accent-amber/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
      <SafeIcon icon={icon} className="w-6 h-6 text-accent-amber" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-muted text-sm leading-relaxed">{desc}</p>
  </div>
);

const SpecItem = ({ title, value }) => (
  <div className="flex justify-between items-center py-3 border-b border-border gap-4">
    <span className="text-muted text-sm shrink-0">{title}</span>
    <span className="text-white font-medium text-sm text-right">{value}</span>
  </div>
);

const PlanCard = ({ name, price, note, points, featured }) => (
  <div
    className={`rounded-none p-6 flex flex-col ${
      featured ? 'bg-card border border-accent-amber/40' : 'bg-card border border-border'
    }`}
  >
    {featured && (
      <span className="text-[10px] uppercase tracking-wider text-accent-amber font-semibold mb-2">
        Best value
      </span>
    )}
    <h3 className="text-lg font-bold text-white mb-1">{name}</h3>
    <div className="text-2xl font-bold text-white mb-1">{price}</div>
    <p className="text-xs text-muted mb-5">{note}</p>
    <ul className="space-y-2 text-sm text-muted flex-grow">
      {points.map((p, i) => (
        <li key={i} className="flex gap-2 items-start">
          <SafeIcon icon={FiCheck} className="w-4 h-4 mt-0.5 shrink-0 text-accent-amber" />
          <span>{p}</span>
        </li>
      ))}
    </ul>
  </div>
);

const PromptWick = () => {
  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-amber/10 border border-accent-amber/20 text-accent-amber text-sm font-medium mb-6">
              <SafeIcon icon={FiFeather} /> Chrome extension
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
              Write rough.<br />
              <span className="text-accent-amber">Ship perfect prompts.</span>
            </h1>
            <p className="text-xl text-muted max-w-3xl mx-auto mb-10 leading-relaxed">
              PromptWick rewrites your half-formed prompt into a clear, structured one, in a side
              panel that sits next to the chat you are already typing in. No copying between tabs,
              no starting again.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
              <ChromeStoreBtn accentColor="bg-accent-amber" className="px-10 py-4 scale-110" href={STORE_LINK} />
              <a
                href={SITE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-8 py-4 rounded-md border border-border bg-card text-white font-semibold hover:border-accent-amber/50 transition-colors"
              >
                <SafeIcon icon={FiGlobe} className="w-5 h-5 text-accent-amber" />
                promptwick.com
              </a>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-2 mt-8 text-sm text-muted">
              <span className="flex items-center gap-2">
                <SafeIcon icon={FiZap} className="text-accent-amber" />
                Free plan: 5 improvements a day.
              </span>
              <span className="flex items-center gap-2">
                <SafeIcon icon={FiShield} className="text-accent-amber" />
                No ads. Your prompts are never resold.
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Preview */}
      <section className="py-10">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-none overflow-hidden border border-border shadow-2xl shadow-accent-amber/10"
          >
            <img
              src="/products/promptwick.svg"
              alt="A rough one-line prompt on the left, rewritten into a structured prompt with role, task and constraints on the right"
              className="w-full h-auto"
            />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Built into the page, not beside it</h2>
            <p className="text-muted">Everything happens in Chrome&apos;s own side panel, so nothing covers the chat.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard
              icon={FiSidebar}
              title="Native side panel"
              desc="Chrome's own panel stays open while you work. One click on the toolbar icon opens and closes it."
            />
            <FeatureCard
              icon={FiLayers}
              title="Templates and variables"
              desc="Type / for a saved template or {{ for a variable. Define your brand and tone once and reuse them everywhere."
            />
            <FeatureCard
              icon={FiZap}
              title="Deep improve"
              desc="A stronger multi-pass rewrite for the prompts that matter, on top of the standard single pass."
            />
            <FeatureCard
              icon={FiClock}
              title="Full history"
              desc="Every improvement is saved and reusable. Nothing good gets lost in a scrollback."
            />
          </div>
        </div>
      </section>

      {/* Where it works */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Where it works</h2>
          <p className="text-muted mb-12 max-w-2xl mx-auto">
            The button appears only on these sites, and only once you start typing in the prompt
            box. Everywhere else the extension does nothing at all.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {SITES.map((site) => (
              <span
                key={site}
                className="px-5 py-2 rounded-full bg-card border border-border text-sm text-primary"
              >
                {site}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Start free</h2>
            <p className="text-muted">
              Every plan is billed in USD.{' '}
              <a
                href={PRICING_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white underline decoration-border hover:decoration-white"
              >
                Current prices and limits live on promptwick.com
              </a>
              .
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <PlanCard
              name="Free"
              price="$0"
              note="No card needed"
              points={['5 improvements a day', '50 a month', 'Full template library']}
            />
            <PlanCard
              name="Lite"
              price="$6.99"
              note="per month"
              points={['Higher daily limit', 'Saved variables', 'Prompt history']}
            />
            <PlanCard
              name="Pro"
              price="$9.99"
              note="per month"
              featured
              points={['Highest daily limit', 'Deep multi-pass rewrite', 'Premium templates']}
            />
            <PlanCard
              name="Pay once"
              price="$58.99"
              note="one-time, launch special"
              points={['Pro features', 'No recurring charge', 'Launch pricing']}
            />
          </div>
        </div>
      </section>

      {/* Privacy + specs */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-20 items-center">
            <div className="flex-1 space-y-8">
              <h2 className="text-4xl font-bold text-white leading-tight">
                An account, and nothing more.<br />
                <span className="text-accent-amber">Here is exactly what that means.</span>
              </h2>
              <p className="text-muted">
                PromptWick is the one thing we make that needs a server, because the rewrite happens
                there. So it is worth being precise about what leaves your browser.
              </p>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-accent-amber/10 flex items-center justify-center text-accent-amber">
                    <SafeIcon icon={FiCheck} />
                  </div>
                  <p className="text-muted">
                    <strong className="text-white">Only the prompt you improve:</strong> the extension
                    reads nothing else on the page and sends nothing until you press the button.
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-accent-amber/10 flex items-center justify-center text-accent-amber">
                    <SafeIcon icon={FiCheck} />
                  </div>
                  <p className="text-muted">
                    <strong className="text-white">Sign-in is optional:</strong> the free plan works on
                    an anonymous device id. Signing in with Google exists so your library follows you
                    between machines.
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-accent-amber/10 flex items-center justify-center text-accent-amber">
                    <SafeIcon icon={FiCheck} />
                  </div>
                  <p className="text-muted">
                    <strong className="text-white">No ad networks, no resale:</strong> what you write is
                    never sold, and there is no third-party analytics inside the extension.
                  </p>
                </div>
              </div>
              <Link
                to="/privacy/promptwick"
                className="inline-flex items-center gap-2 text-white font-medium hover:text-accent-amber transition-colors"
              >
                Read the PromptWick privacy policy <SafeIcon icon={FiExternalLink} />
              </Link>
            </div>

            <div className="flex-1 w-full max-w-md">
              <div className="bg-card border border-border rounded-none p-8 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <SafeIcon icon={FiList} className="text-accent-amber" /> Tech Specs
                </h3>
                <div className="space-y-1">
                  <SpecItem title="Platform" value="Chrome 116+" />
                  <SpecItem title="Manifest" value="MV3" />
                  <SpecItem title="Permissions" value="storage, sidePanel, identity" />
                  <SpecItem title="Supported sites" value="11 AI chats" />
                  <SpecItem title="Package size" value="Under 100 KB" />
                  <SpecItem title="Remote code" value="None" />
                </div>
                <div className="mt-8 pt-6 border-t border-border">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-white font-bold">Free plan</span>
                    <span className="text-accent-amber font-bold text-xl">$0</span>
                  </div>
                  <p className="text-xs text-muted mb-6">
                    Five improvements a day with no card and no account. Upgrade only if you run out.
                  </p>
                  <a
                    href={STORE_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-md bg-white text-background font-bold hover:bg-gray-200 transition-colors"
                  >
                    Add to Chrome
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-accent-amber/5 -z-10" />
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-white mb-6">Stop rewriting your own prompts.</h2>
          <p className="text-muted mb-10">
            Install it, type the way you already type, and let the panel do the structuring. The free
            plan needs no card and no account.
          </p>
          <ChromeStoreBtn accentColor="bg-accent-amber" className="mx-auto px-12 py-5 scale-110" href={STORE_LINK} />
        </div>
      </section>
    </div>
  );
};

export default PromptWick;
